import apiService from './apiService';
import { GET } from './executer';
import type { EventSource } from './eventsService';

export interface RadarTile {
  timestamp: string;
  url: string;
}

export interface RadarTilesData {
  baseUrl: string;
  tiles: RadarTile[];
  timezone: string;
  source?: string;
}

interface RadarTilesQuery {
  season_year: number;
  circuit_id: string;
  start_date: string;
  source?: EventSource | 'legacy-api';
  start_timestamp?: string; // Optional: YYYYMMDDHHmmss format
  end_timestamp?: string; // Optional: YYYYMMDDHHmmss format
  bypass_cache?: boolean;
}

class RadarTilesService {
  /**
   * Fetch radar tiles for an event
   * Optionally filter by timestamp range to reduce data transfer
   */
  async getRadarTiles(query: RadarTilesQuery): Promise<RadarTilesData> {
    const { season_year, circuit_id, start_date, source = 'legacy', start_timestamp, end_timestamp, bypass_cache } = query;
    
    const queryParams = new URLSearchParams({
      season_year: String(season_year),
      circuit_id,
      start_date,
      source,
    });

    // Add optional timestamp filters
    if (start_timestamp) {
      queryParams.append('start_timestamp', start_timestamp);
    }
    if (end_timestamp) {
      queryParams.append('end_timestamp', end_timestamp);
    }
    if (bypass_cache) {
      queryParams.append('bypass_cache', 'true');
    }

    const response = await apiService.client.execute<RadarTilesData>(
      GET,
      `/api/radar-tiles?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch radar tiles');
    }

    // The Executer already extracts response.data.data, so we just return response.data
    return response.data;
  }

  /**
   * Fetch L2 radar tiles (from fia-portal-tiles-2 bucket).
   * Returns signed URLs since the bucket is not public.
   */
  async getRadarTilesL2(query: { start_timestamp: string; end_timestamp: string }): Promise<RadarTilesData> {
    const { start_timestamp, end_timestamp } = query;

    const queryParams = new URLSearchParams({ start_timestamp, end_timestamp });

    const response = await apiService.client.execute<RadarTilesData>(
      GET,
      `/api/radar-tiles-l2?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch L2 radar tiles');
    }

    return response.data;
  }

  /**
   * Fetch L2 numeric (.pngd) radar tiles for client-side decoding and colorization.
   */
  async getRadarTilesL2Pngd(query: { start_timestamp: string; end_timestamp: string }): Promise<RadarTilesData> {
    const { start_timestamp, end_timestamp } = query;

    const queryParams = new URLSearchParams({ start_timestamp, end_timestamp });

    const response = await apiService.client.execute<RadarTilesData>(
      GET,
      `/api/radar-tiles-l2-pngd?${queryParams.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error('Failed to fetch L2 PNGD radar tiles');
    }

    return response.data;
  }
}

const radarTilesService = new RadarTilesService();
export default radarTilesService;

